import { PopoverForOverflownText, TableColumn, TableRow } from '@lifebit-ai/lds'
import React from 'react'

import startCase from 'lodash/startCase'
import * as ExportRequestTableStyles from 'modules/requests/ExportAndAccessRequestsTable/RequestsTable.styles'
import { AssigneeColumnContent } from 'modules/requests/components/AssigneeColumnContent'
import { ImportRequestStatus as ImportRequestStatusBadge } from 'modules/requests/components/ImportRequestStatus'
import { LabelsColumnContent } from 'modules/requests/components/LabelsColumnContent'
import {
  formatImportRequestObjectCountsSummary,
  isExternalImportRequest,
  isUploadedImportRequest
} from 'services/import-requests/importRequestUtils'
import type { ImportRequestVerificationListItem } from 'services/import-requests/types'
import { formatDate } from 'utils/date'

export const getImportRequestsTableColumns = (): TableColumn<ImportRequestVerificationListItem>[] => [
  {
    title: 'Status',
    id: 'status',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      return <ImportRequestStatusBadge status={row.original.status} />
    },
    width: 170
  },
  {
    title: 'Request type',
    id: 'requestType',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      if (isExternalImportRequest(row.original)) {
        return 'External'
      }

      return isUploadedImportRequest(row.original) ? 'Upload' : '-'
    },
    width: 140
  },
  {
    title: 'Scan result',
    id: 'scanResult',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      if (isExternalImportRequest(row.original)) {
        return formatImportRequestObjectCountsSummary(row.original.counts)
      }

      return isUploadedImportRequest(row.original) ? startCase(row.original.scanResult) : '-'
    },
    // Wide enough for the external summary ("N clean, N threat, N unsupported") on one line.
    width: 260
  },
  {
    title: 'Source',
    id: 'source',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      const sourceLabel = isExternalImportRequest(row.original) ? row.original.sourceUri : row.original.fileName

      return (
        <ExportRequestTableStyles.DatasetFileCellContainer>
          <ExportRequestTableStyles.FileIcon aria-hidden />
          <ExportRequestTableStyles.DatasetFileCellText>
            <PopoverForOverflownText title={sourceLabel}>{sourceLabel}</PopoverForOverflownText>
          </ExportRequestTableStyles.DatasetFileCellText>
        </ExportRequestTableStyles.DatasetFileCellContainer>
      )
    },
    width: 400
  },
  {
    title: 'Labels',
    id: 'labelIds',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      return <LabelsColumnContent labels={row.original.labels} />
    },
    width: 200
  },
  {
    title: 'Workspace',
    id: 'workspaceName',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      const workspaceName = row.original.workspaceName ?? '-'

      return <PopoverForOverflownText title={workspaceName}>{workspaceName}</PopoverForOverflownText>
    },
    width: 180
  },
  {
    title: 'Workspace ID',
    id: 'workspaceId',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      return row.original.workspaceId
    },
    width: 230
  },
  {
    title: 'Requested by',
    id: 'requestedBy',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      const email = row.original.requestingUser?.email ?? '-'

      return <PopoverForOverflownText title={email}>{email}</PopoverForOverflownText>
    },
    width: 200
  },
  {
    title: 'Assignee',
    id: 'assignedTo',
    render: (row: TableRow<ImportRequestVerificationListItem>) => (
      <AssigneeColumnContent assignedTo={row.original.assignedTo} />
    ),
    width: 200
  },
  {
    title: 'Requested on',
    id: 'createdAt',
    render: (row: TableRow<ImportRequestVerificationListItem>) => {
      return formatDate(row.original.createdAt)
    },
    width: 200
  }
]
